import { useEffect, useRef } from 'react';
import { draftStorageService } from '../services/draftStorageService';

/**
 * Auto-saves form data to localStorage with debouncing
 * @param {string} sessionGuid - The session GUID
 * @param {string} formId - The form ID
 * @param {object} formData - The form data to save
 * @param {number} delay - Debounce delay in ms (default: 3000)
 * @param {boolean} enabled - Whether auto-save is enabled (default: true)
 */
export const useAutoSaveDraft = (sessionGuid, formId, formData, delay = 3000, enabled = true) => {
  const timeoutRef = useRef(null);
  const previousDataRef = useRef(null);

  useEffect(() => {
    console.log('⏱️ AUTO-SAVE HOOK:', {
      enabled,
      sessionGuid,
      formId,
      hasData: Object.keys(formData).length > 0,
      dataKeys: Object.keys(formData)
    });

    if (!enabled || !sessionGuid || !formId) {
      console.log('⏸️ Auto-save skipped (disabled or missing params)');
      return;
    }

    // Don't save if data hasn't changed
    const currentJson = JSON.stringify(formData);
    const previousJson = JSON.stringify(previousDataRef.current);
    if (currentJson === previousJson) {
      console.log('⏸️ Auto-save skipped (data unchanged)');
      return;
    }

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    console.log(`⏳ Auto-save scheduled in ${delay}ms`);
    timeoutRef.current = setTimeout(() => {
      console.log('⚡ AUTO-SAVE TRIGGERED at', new Date().toLocaleTimeString());
      draftStorageService.saveDraft(sessionGuid, formId, formData);
      previousDataRef.current = formData;
    }, delay);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sessionGuid, formId, formData, delay, enabled]);
};
